import React, { ProviderExoticComponent, ProviderProps, useEffect, useMemo, useRef, useState } from 'react'
import { atom, useRecoilValue } from 'recoil'
import styled from '@emotion/styled'
import { DeckGL, DeckGLContextValue, DeckGLRef } from '@deck.gl/react/typed'
import { MapGlContext } from 'src/components/map/mapbox/map-gl-context'
import { MapRef, Map as StaticMap } from 'react-map-gl'
import { DeckProps, MapViewState } from '@deck.gl/core/typed'
import { MapContext } from 'src/components/map/mapbox/map-context'
import { Position } from 'src/common-services'
import {
  MAPBOX_STYLES,
  MAPBOX_TOKEN,
  MAP_CONTROL_ZOOM_BTN_DURATION,
  MAP_CONTROL_ZOOM_BTN_STEP,
  MAP_CONTROL_ZOOM_MAX,
  MAP_CONTROL_ZOOM_MIN,
  MAP_DEFAULT_BEARING,
  MAP_DEFAULT_CENTER_POINT,
  MAP_DEFAULT_CONTROLS,
  MAP_DEFAULT_CURSOR,
  MAP_DEFAULT_HEIGHT,
  MAP_DEFAULT_PITCH,
  MAP_DEFAULT_STYLE,
  MAP_DEFAULT_STYLE_CONFIG,
  MAP_DEFAULT_SYNC_DEBOUNCE,
  MAP_DEFAULT_WIDTH,
  MAP_DEFAULT_ZOOM,
} from 'src/components/map/mapbox/config'
import { Controller, Layer, PickingInfo } from '@deck.gl/core/typed'
import { DeckContext } from 'src/components/map-layers/deck'
import { Bounds, MapStyle, MapStyleConfig } from 'src/components/map-new/types'
import { boundsToViewport, isDistanceEqual, isZoomEqual, viewportToBounds } from 'src/components/map/utils'
import { Levels } from 'src/components/theme'
import { MapComponentControls } from 'src/components/map/mapbox/controls'
import { updateContainerDimensions } from 'src/components/map/mapbox/mapbox-patchs'
import { useElement } from 'src/components/report/contexts/element'
import { useIsMapEditor } from 'src/components/map-editor/contexts/editor'
import { ControllerOptions } from 'src/types/map-editor'
import { DeckContextType } from 'src/types/deck'
import { MapboxMapContext } from 'src/hooks/use-mapbox-map'
import { TILE_AUTH_HEADER_KEY, TILE_VERIFY_HEADER_KEY } from 'src/constants/map-tiles'
import { useMapOverlayContext } from 'src/layout/modals/map-overlay'
import { Portal } from '@mui/material'
import { MapExpandControl } from 'src/components/map/mapbox/controls/expand'
import { AERIAL_TOGGLE_ZOOM_THRESHOLD } from 'src/components/map/google-map/map-type-toggle'

import * as tilesAtoms from 'src/atoms/tiles'
import * as mainMapAtoms from 'src/atoms/main-map'

export type MapComponentProps = {
  mapStyle?: MapStyle
  mapStyleConfig?: MapStyleConfig
  width?: string
  height?: string
  cursor?: string
  center?: Position
  zoom?: number
  bearing?: number
  pitch?: number
  children?: React.ReactNode
  controls?: boolean
  controller?: null | Controller<any> | ControllerOptions | boolean
  onViewStateChange?: (viewState: MapViewState) => void
  onCenterChange?: (center: Position) => void
  onBoundsChange?: (bounds: Bounds) => void
  onZoomChange?: (zoom: number) => void
  onBearingChange?: (bearing: number) => void
  onPitchChange?: (pitch: number) => void
  onMapStyleChange?: (mapStyle: MapStyle) => void
  onMapStyleConfigChange?: (mapStyleConfig: MapStyleConfig) => void
  getCursor?: DeckProps['getCursor']
  pickingRadius?: DeckProps['pickingRadius']
  getTooltip?: DeckProps['getTooltip']
  onClick?: (info: PickingInfo, event: MouseEvent) => void
  enableControls?: boolean
  mapType?: 'main' | 'builder'
  attributionControl?: boolean
  expanded?: boolean
  setExpanded?: (expanded: boolean) => void
  disableZoomControl?: boolean
  // This prop prevents querying for the authentication details
  // for tile requests altogether
  noTiles?: boolean
}

type MapWrapperProps = {
  cursor?: string
}

type InteractionState = {
  inTransition?: boolean | undefined
  isDragging?: boolean | undefined
  isPanning?: boolean | undefined
  isRotating?: boolean | undefined
  isZooming?: boolean | undefined
}

const emptyControllerProps = {
  scrollZoom: false,
  dragPan: false,
  dragRotate: false,
  doubleClickZoom: false,
  touchZoom: false,
  touchRotate: false,
  keyboard: false,
  inertia: false,
  dragMode: 'pan',
}

const noop = () => {}

const noopAtom = atom<undefined>({
  key: 'noop',
  default: undefined,
})

const MapWrapper = styled.div<MapWrapperProps>`
  position: relative;
  overflow: hidden;
  cursor: ${(props) => props.cursor};

  & #view-default-view {
    position: relative;
    cursor: ${(props) => props.cursor} !important;
  }

  & .mapboxgl-ctrl-attrib {
    opacity: 0.7;
    padding: 2px 5px !important;
  }
  & .mapboxgl-ctrl-attrib-button {
    display: none !important;
  }
  & .mapboxgl-ctrl-attrib-inner {
    display: block !important;
  }
  & .mapbox-improve-map {
    display: none !important;
  }
`

const MapContent = styled.div`
  position: relative;
  z-index: ${Levels.base};
  width: 100%;
  height: 100%;
`

const MapImageRemplacement = styled.img`
  position: absolute;
  top: 0;
  left: 0;
`

export const MapComponent = (props: MapComponentProps) => {
  const [expanded, setExpanded] = useState(false)

  const context = useMapOverlayContext()

  useEffect(() => {
    context.setActive(expanded)
  }, [expanded])

  return (
    <>
      {expanded ? (
        <Portal container={context.portalRef.current}>
          <MapComponentInner {...props} expanded={true} setExpanded={setExpanded}></MapComponentInner>
        </Portal>
      ) : null}
      <MapComponentInner {...props} setExpanded={setExpanded}></MapComponentInner>
    </>
  )
}

export const MapComponentInner = (props: MapComponentProps) => {
  const {
    mapStyle = MAP_DEFAULT_STYLE,
    mapStyleConfig = MAP_DEFAULT_STYLE_CONFIG,
    width = MAP_DEFAULT_WIDTH,
    height = MAP_DEFAULT_HEIGHT,
    cursor = MAP_DEFAULT_CURSOR,
    zoom = MAP_DEFAULT_ZOOM,
    bearing = MAP_DEFAULT_BEARING,
    pitch = MAP_DEFAULT_PITCH,
    center = MAP_DEFAULT_CENTER_POINT,
    controls = MAP_DEFAULT_CONTROLS,
    controller = true,
    onClick = noop,
    children,
    getCursor,
    pickingRadius,
    getTooltip,
    enableControls = true,
    mapType = 'builder',
    expanded = false,
    setExpanded = noop,
    disableZoomControl,
    noTiles = false,
  } = props

  // Sequence Printing
  const [printingMapImage] = useState<string | null>(null)
  const [printingDeckImage] = useState<string | null>(null)

  // Aerial Toggle
  const isAerialEnabled = useRecoilValue(mainMapAtoms.isAerialEnabled)

  // Map Ref
  const mapWrapperRef = useRef<HTMLDivElement>(null)
  const deckRef = useRef<DeckGLRef>(null)
  const mapRef = useRef<MapRef | null>(null)
  const layersRef = useRef<Map<string, Layer<any>>>(new Map())
  const mapLoaded = useRef(false)
  const initialBounds = useRef<Bounds | null>(null)
  const mapInTransition = useRef<number | null>(null)

  // Tiles
  const [map, setMap] = useState<mapboxgl.Map | null>(null)
  const tilesAuth = useRecoilValue(noTiles ? noopAtom : tilesAtoms.auth)

  const transformRequest = (url: string, resourceType: string) => {
    if (tilesAuth == undefined) {
      return {
        url,
      }
    }
    // this gets called with every single url that mapbox loads
    // including images, styles, etc...we only want to include our token with tile requests
    const origin = tilesAuth.host ? new URL(tilesAuth.host).origin : window.location.origin
    if (resourceType === 'Tile' && url.startsWith(origin)) {
      return {
        url,
        headers: {
          [TILE_AUTH_HEADER_KEY]: tilesAuth.token,
          [TILE_VERIFY_HEADER_KEY]: tilesAuth.verify,
        },
      }
    }
    return {
      url,
    }
  }

  // MapStyle
  const [internalMapStyle, setInternalMapStyle] = useState(mapStyle)

  const onMapStyleChange = (mapStyle: MapStyle) => {
    setInternalMapStyle(mapStyle)
    if (props.onMapStyleChange) {
      props.onMapStyleChange(mapStyle)
    }
  }

  // MapStyleConfig
  const [internalMapStyleConfig, setInternalMapStyleConfig] = useState(mapStyleConfig)
  const appliedMapStyleConfig = useRef<boolean>(false)

  const onMapStyleConfigChange = (mapStyleConfig: MapStyleConfig) => {
    setInternalMapStyleConfig(mapStyleConfig)
    if (props.onMapStyleConfigChange) {
      props.onMapStyleConfigChange(mapStyleConfig)
    }
  }

  useEffect(() => {
    appliedMapStyleConfig.current = false
    applyMapStyleConfig()
  }, [internalMapStyleConfig])

  // Map View State
  const [viewState, setViewState] = useState<MapViewState>({
    longitude: center[0],
    latitude: center[1],
    zoom: zoom,
    bearing: bearing,
    pitch: pitch,
  })
  const viewStateRef = useRef(viewState)

  const syncBounds = (newViewState: MapViewState) => {
    const newBounds = viewportToBounds(newViewState)
    if (props.onBoundsChange) {
      props.onBoundsChange(newBounds)
    }
  }

  const syncViewState = (newViewState: MapViewState) => {
    if (props.onViewStateChange) {
      props.onViewStateChange(newViewState)
    }
    if (props.onCenterChange) {
      props.onCenterChange([
        newViewState.longitude || MAP_DEFAULT_CENTER_POINT[0],
        newViewState.latitude || MAP_DEFAULT_CENTER_POINT[1],
      ])
    }
    if (props.onBoundsChange) {
      syncBounds(newViewState)
    }
    if (props.onZoomChange && newViewState.zoom) {
      props.onZoomChange(newViewState.zoom)
    }
    if (props.onBearingChange && newViewState.bearing) {
      props.onBearingChange(newViewState.bearing)
    }
    if (props.onPitchChange && newViewState.pitch) {
      props.onPitchChange(newViewState.pitch)
    }
  }

  const onFinishTransition = () => {
    if (!mapInTransition.current) {
      return
    }
    const now = Date.now()
    const diff = now - mapInTransition.current
    if (diff >= MAP_DEFAULT_SYNC_DEBOUNCE) {
      mapInTransition.current = null
      syncViewState(viewStateRef.current)
    }
  }

  const onViewStateChange = (newViewState: MapViewState, interaction: InteractionState) => {
    viewStateRef.current = newViewState

    // Restrict zoom level when aerial is enabled
    const zoom = newViewState.zoom || 0
    const calcZoom = isAerialEnabled && zoom < AERIAL_TOGGLE_ZOOM_THRESHOLD ? AERIAL_TOGGLE_ZOOM_THRESHOLD : zoom

    setViewState({
      ...newViewState,
      zoom: calcZoom,
    })

    // Only dispatch onBoundsChange when the user is not interacting with the map
    const isInteracting = Object.values(interaction).some((value) => value)
    mapInTransition.current = interaction.inTransition ? Date.now() : null
    if (isInteracting) {
      // Trigger a sync at the end of the transition
      setTimeout(onFinishTransition, MAP_DEFAULT_SYNC_DEBOUNCE)
      return
    }
    syncViewState(newViewState)
  }

  // MapControl
  const [internalController, setInternalController] = useState(controller)

  useEffect(() => {
    if (controller !== internalController) {
      setInternalController(controller)
    }
  }, [controller])

  const element = useElement()
  const isEditor = useIsMapEditor()

  const enableMapControl = useMemo(() => {
    if (!enableControls) {
      return false
    }
    if (isEditor) {
      return true
    }
    if (element) {
      return element.status.edited && !element.status.preview
    }
    return controls
  }, [element, isEditor, enableControls])

  // This is a HACK to prevent the map move when a keyboard ever is triggered
  const controllerOptions: any = internalController || emptyControllerProps

  const enableController = () => {
    setInternalController(true)
  }

  const disableController = () => {
    setInternalController(false)
  }

  const fitToBounds = (bounds: Bounds) => {
    if (!mapLoaded.current) {
      initialBounds.current = bounds
      return
    }
    const newViewState = boundsToViewport(viewState, bounds)
    setViewState(newViewState)
  }

  // Layers
  const [layers, setLayers] = React.useState<Layer<any>[]>([])
  const manager = React.useMemo((): DeckContextType => {
    return {
      setLayer: (layer: Layer<any>) => {
        layersRef.current.set(layer.id, layer)
        setLayers(Array.from(layersRef.current.values()))
      },
      removeLayer: (layer: Layer<any>) => {
        layersRef.current.delete(layer.id)
        setLayers(Array.from(layersRef.current.values()))
      },
      pickObject: (x, y, layerId) => {
        if (!layerId) {
          return
        }
        deckRef.current ? deckRef.current.pickObject({ x, y, layerIds: [layerId] }) : undefined
      },
    }
  }, [deckRef.current])

  // Map Style Config
  const applyMapStyleConfig = () => {
    if (!mapRef.current || appliedMapStyleConfig.current) {
      return
    }

    const map = mapRef.current.getMap()
    let applied = true

    // Apply showRoadLabels
    const roadLabelLayer = 'road-label'
    if (map.getLayer(roadLabelLayer)) {
      map.setLayoutProperty(roadLabelLayer, 'visibility', internalMapStyleConfig.showRoadLabels ? 'visible' : 'none')
    } else {
      applied = false
    }

    // Apply showPlaceLabels
    const placeLabelsLayers = [
      'settlement-subdivision-label',
      'settlement-minor-label',
      'settlement-major-label',
      'state-label',
      'country-label',
      'continent-label',
    ]
    placeLabelsLayers.forEach((layer) => {
      if (map.getLayer(layer)) {
        map.setLayoutProperty(layer, 'visibility', internalMapStyleConfig.showPlaceLabels ? 'visible' : 'none')
      } else {
        applied = false
      }
    })

    // Apply showPlaces
    const poiLabelLayer = 'poi-label'
    if (map.getLayer(poiLabelLayer)) {
      map.setLayoutProperty(poiLabelLayer, 'visibility', internalMapStyleConfig.showPlaces ? 'visible' : 'none')
    } else {
      applied = false
    }

    // Apply showHighwayIdentifiers
    const roadNumberShieldLayer = 'road-number-shield'
    if (map.getLayer(roadNumberShieldLayer)) {
      map.setLayoutProperty(
        roadNumberShieldLayer,
        'visibility',
        internalMapStyleConfig.showHighwayIdentifiers ? 'visible' : 'none'
      )
    } else {
      applied = false
    }

    // Apply showTransportationLabels
    const transportationLabelsLayers = ['transit-label', 'airport-label']
    transportationLabelsLayers.forEach((layer) => {
      if (map.getLayer(layer)) {
        map.setLayoutProperty(layer, 'visibility', internalMapStyleConfig.showTransportationLabels ? 'visible' : 'none')
      } else {
        applied = false
      }
    })

    // Apply showNaturalFeatures
    const naturalLabelsLayers = [
      'waterway-label',
      'natural-line-label',
      'natural-point-label',
      'water-line-label',
      'water-point-label',
    ]
    naturalLabelsLayers.forEach((layer) => {
      if (map.getLayer(layer)) {
        map.setLayoutProperty(layer, 'visibility', internalMapStyleConfig.showNaturalFeatures ? 'visible' : 'none')
      } else {
        applied = false
      }
    })

    // Disable Road Exit Shields
    const roadExitShieldLayer = 'road-exit-shield'
    if (map.getLayer(roadExitShieldLayer)) {
      map.setLayoutProperty(roadExitShieldLayer, 'visibility', 'none')
    } else {
      applied = false
    }

    // Debug Layers
    // console.log('layes', map.getStyle().layers)

    appliedMapStyleConfig.current = applied
  }

  // On Map Mount
  const onMapMount = (ref: MapRef | null) => {
    if (ref) {
      // Override the mapbox-gl-js default behavior for the new version of this function
      const map = ref.getMap()
      // @ts-ignore
      map._updateContainerDimensions = updateContainerDimensions
      map.resize()
      mapRef.current = ref

      // Apply the override Styles after the map loaded the styles
      map.on('styledata', applyMapStyleConfig)
    }
  }

  // On MapLoad
  const onMapLoad = () => {
    mapLoaded.current = true
    // this animates the map to the bounds provided when the map first loads
    if (initialBounds.current) {
      fitToBounds(initialBounds.current)
    }
    if (mapRef.current) {
      const map = mapRef.current.getMap()
      setMap(map)
    }
  }

  const onMapIdle = () => {
    return
  }

  // Controls
  const onZoomIn = () => {
    setViewState((viewState) => {
      return {
        ...viewState,
        zoom: Math.min((viewState.zoom || 0) + MAP_CONTROL_ZOOM_BTN_STEP, MAP_CONTROL_ZOOM_MAX),
        transitionDuration: MAP_CONTROL_ZOOM_BTN_DURATION,
      }
    })
  }

  const onZoomOut = () => {
    const zoom = Math.max((viewState.zoom || 0) - MAP_CONTROL_ZOOM_BTN_STEP, MAP_CONTROL_ZOOM_MIN)
    const calcZoom = isAerialEnabled && zoom < AERIAL_TOGGLE_ZOOM_THRESHOLD ? AERIAL_TOGGLE_ZOOM_THRESHOLD : zoom
    setViewState((viewState) => {
      return {
        ...viewState,
        zoom: calcZoom,
        transitionDuration: MAP_CONTROL_ZOOM_BTN_DURATION,
      }
    })
  }

  const on3dReset = () => {
    setViewState((viewState) => {
      return {
        ...viewState,
        bearing: 0,
        pitch: 0,
        transitionDuration: MAP_CONTROL_ZOOM_BTN_DURATION,
      }
    })
    if (props.onBearingChange) {
      props.onBearingChange(0)
    }
    if (props.onPitchChange) {
      props.onPitchChange(0)
    }
  }

  const onMouseClick = (info: PickingInfo, event: any) => {
    onClick(info, event)
  }

  // Update MapStyle
  useEffect(() => {
    setInternalMapStyle(mapStyle)
  }, [mapStyle])

  // Update MapStyleConfig
  useEffect(() => {
    setInternalMapStyleConfig(mapStyleConfig)
  }, [mapStyleConfig.showPlaceLabels, mapStyleConfig.showPlaces, mapStyleConfig.showRoadLabels])

  // Update Map View State
  useEffect(() => {
    if (isZoomEqual(viewState.zoom, zoom)) {
      return
    }
    setViewState((state) => ({ ...state, zoom }))
    syncBounds({ ...viewState, zoom })
  }, [zoom])

  useEffect(() => {
    if (isDistanceEqual(viewState.longitude, center[0]) || isDistanceEqual(viewState.latitude, center[1])) {
      return
    }
    setViewState((state) => ({ ...state, longitude: center[0], latitude: center[1] }))
    syncBounds({ ...viewState, longitude: center[0], latitude: center[1] })
  }, [center])

  useEffect(() => {
    if (viewState.bearing === bearing) {
      return
    }
    setViewState((state) => ({ ...state, bearing }))
  }, [bearing])

  useEffect(() => {
    if (viewState.pitch === pitch) {
      return
    }
    setViewState((state) => ({ ...state, pitch }))
  }, [pitch])

  const widthValue = expanded ? '100%' : width
  const heightValue = expanded ? '100%' : height

  return (
    <MapWrapper ref={mapWrapperRef} cursor={cursor} style={{ width: widthValue, height: heightValue }}>
      <MapContent>
        {printingMapImage && <MapImageRemplacement src={printingMapImage} />}
        {printingDeckImage && <MapImageRemplacement src={printingDeckImage} />}
        <MapContext.Provider
          value={{
            config: {
              enable: enableController,
              disable: disableController,
            },
            fitToBounds: fitToBounds,
            mapType,
          }}>
          <DeckGL
            ref={deckRef}
            width={widthValue}
            height={heightValue}
            ContextProvider={
              MapGlContext.Provider as unknown as ProviderExoticComponent<ProviderProps<DeckGLContextValue>>
            }
            initialViewState={viewState}
            controller={controllerOptions}
            layers={layers}
            onViewStateChange={(args) => onViewStateChange(args.viewState as MapViewState, args.interactionState)}
            getCursor={getCursor}
            onClick={onMouseClick}
            pickingRadius={pickingRadius}
            getTooltip={getTooltip}
            glOptions={{
              stencil: true,
              preserveDrawingBuffer: true,
            }}>
            <>
              <DeckContext.Provider value={manager}>
                {map ? (
                  <MapboxMapContext.Provider value={{ map, style: mapStyle }}>{children}</MapboxMapContext.Provider>
                ) : null}
              </DeckContext.Provider>
              <StaticMap
                ref={onMapMount}
                mapStyle={MAPBOX_STYLES[internalMapStyle]}
                mapboxAccessToken={MAPBOX_TOKEN}
                onLoad={onMapLoad}
                onIdle={onMapIdle}
                preserveDrawingBuffer={true}
                dragPan={true}
                attributionControl={false}
                transformRequest={transformRequest}
                minZoom={isAerialEnabled ? AERIAL_TOGGLE_ZOOM_THRESHOLD : undefined}
              />
            </>
          </DeckGL>
        </MapContext.Provider>
      </MapContent>
      <MapComponentControls
        enabled={enableMapControl}
        viewState={viewState}
        mapStyle={internalMapStyle}
        zoom={zoom}
        onMapStyleChange={onMapStyleChange}
        mapStyleConfig={internalMapStyleConfig}
        onMapStyleConfigChange={onMapStyleConfigChange}
        onZoomIn={onZoomIn}
        onZoomOut={onZoomOut}
        on3dReset={on3dReset}
        disableZoomControl={disableZoomControl}
      />
      <MapExpandControl expanded={expanded} setExpanded={setExpanded} />
    </MapWrapper>
  )
}
